import { gql } from "@apollo/client";
import { Database, Q } from "@nozbe/watermelondb";
import {
  LayoutNodeDeepNestingFragment,
  LayoutNodeLinkDeepNestingFragment,
} from "@towersystems/roam-common/lib/generated-graphql-fragment";
import {
  LayoutNode as LayoutNodeEntity,
  ShiftLayoutNodesInput,
} from "@towersystems/roam-common/lib/generated-types";
import { getNodeShiftParams } from "@towersystems/roam-common/lib/shared-utils";
import { client } from "../../../apollo";
import { default as LayoutNode } from "../../data/model/LayoutNode";
import { AsyncLayoutActionDefinition } from "../types";
import LayoutNodeModel from "../../data/model/LayoutNode";
import { LayoutNodeFilterDeepNestingFragment } from "@towersystems/roam-common/lib/generated-graphql-fragment";

export const shiftLayoutNodesMutation = gql`
  ${LayoutNodeFilterDeepNestingFragment}
  ${LayoutNodeLinkDeepNestingFragment}
  ${LayoutNodeDeepNestingFragment}
  mutation ShiftLayoutNodes($input: ShiftLayoutNodesInput!) {
    shiftLayoutNodes(input: $input) {
      ...LayoutNodeDeepNesting
    }
  }
`;

export const shiftLayoutKeysDefinition = new AsyncLayoutActionDefinition<
  LayoutNodeEntity,
  LayoutNodeModel,
  (
    database: Database,
    shiftLayoutNodesInput: ShiftLayoutNodesInput
  ) => Promise<any>,
  (shiftLayoutNodesInput: ShiftLayoutNodesInput) => Promise<any>
>({
  localAction: async (
    database: Database,
    shiftLayoutNodesInput: ShiftLayoutNodesInput
  ) => {
    const targetNode = await database.collections
      .get<LayoutNode>("layout_nodes")
      .find(shiftLayoutNodesInput.id);

    const shiftParams = getNodeShiftParams(
      targetNode.position,
      shiftLayoutNodesInput.position
    );

    const nodesToShift = await database.collections
      .get<LayoutNode>("layout_nodes")
      .query(
        Q.where("layout_id", targetNode.layout.id),
        Q.and(
          Q.where("position", Q.between(shiftParams.start, shiftParams.end))
        )
      );

    await database.write(async () => {
      await database.batch(
        ...nodesToShift.map((layoutNode) =>
          layoutNode.prepareUpdate((currentNode) => {
            currentNode.position = currentNode.position + shiftParams.offset;
          })
        ),
        targetNode.prepareUpdate((node) => {
          node.position = shiftLayoutNodesInput.position;
        })
      );
    });

    const layoutNodes = await database.collections
      .get<LayoutNode>("layout_nodes")
      .query(Q.where("layout_id", targetNode.layout.id));

    return layoutNodes;
  },
  remoteAction: async (shiftLayoutNodesInput: ShiftLayoutNodesInput) => {
    return client
      .mutate({
        variables: { input: shiftLayoutNodesInput },
        mutation: shiftLayoutNodesMutation,
      })
      .then((result) => {
        return result.data?.shiftLayoutNodes;
      });
  },
});
